import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { CircularProgress, MenuItem } from '@mui/material'
import { useIntl } from 'react-intl'
import { useVenti } from 'venti'
import { useMutation } from '@tanstack/react-query'
import { useLoanContext } from '../contexts/LoanContext'
import useUser from '../hooks/useUser'
import useErrorHandler from '../hooks/useErrorHandler'
import { useAlert } from '../hooks'
import { TheBigPOSApi } from '../lib/TheBigPOSClient'
import { navigationLinking } from '../services/navigation'
import LoanLockedAlert from './loan/LoanLockedAlert'
import LoanSummary from './loan/LoanSummary'
import ActionMenu from './ActionMenu'
import Breadcrumbs, {
	BreadCrumbItem,
} from './breadcrumbs/Breadcrumbs'
import { useAppContext } from './AppContext'
import { useAppContextActions } from './AppContext/AppHooks'
import { getTheme } from '../config'

type SubHeaderProps = {
	breadcrumbs?: BreadCrumbItem[]
}

const theme = getTheme()

export default function SubHeader({ breadcrumbs }: SubHeaderProps) {
	const { loan, loanData, refetch, activeLoanNavigationCrumb } =
		useLoanContext()
	const { isLoanAdmin } = useUser()
	const navigate = useNavigate()
	const { formatMessage } = useIntl()
	const { handleError } = useErrorHandler()
	const { alert } = useAlert()
	const { applyImpersonationRequest, applyUser } =
		useAppContextActions()
	const { setActiveLoanId } = useLoanContext()
	const { state } = useAppContext()
	const ventiState = useVenti()
	const { impersonationRequest, user } = state
	const hasLoan = !!loan?.loanID
	function stopImpersonating() {
		applyImpersonationRequest({
			...(impersonationRequest ?? {}),
			status: 'Stopped',
		})
		applyUser({ ...user })
		setActiveLoanId('')
		ventiState.set(theme.storageKeys.loanTasks, null)
		ventiState.set(theme.storageKeys.loanDocs, null)
		navigate(`/${navigationLinking.Portal}`)
	}
	const rootNavigationCrumb: BreadCrumbItem = {
		label: '',
		value: 'home',
		onClick: stopImpersonating,
	}
	const baseNavigationCrumbs = (function getBaseNavigationCrumbs() {
		if (loan?.loanID)
			return [rootNavigationCrumb, activeLoanNavigationCrumb]

		return [rootNavigationCrumb]
	})()

	const [isActionMenuOpen, setIsActionMenuOpen] = useState(false)

	const handleActionMenuOpen = () => {
		setIsActionMenuOpen(true)
	}

	const handleActionMenuClose = () => {
		setIsActionMenuOpen(false)
	}

	const isPosLoan = loanData?.IsPOSLoan ?? false

	const handleLoanSettingsClick = () => {
		navigate(`/${navigationLinking.LoanSettings}`)
	}

	const importFromEncompassMutation = useMutation({
		mutationFn: () => {
			if (!hasLoan) {
				return Promise.reject(
					new Error(
						formatMessage({
							id: 'loans.los_id_is_required_to_perform_operation',
						})
					)
				)
			}
			return TheBigPOSApi.importLoanFromLos(loan.loanID)
		},
	})

	const handleSyncWithLosClick = async () => {
		try {
			await importFromEncompassMutation.mutateAsync()
			if (refetch) {
				await refetch()
			}
		} catch (e) {
			const errorResponse = handleError(e)
			alert(errorResponse.message, { severity: 'error' })
		} finally {
			handleActionMenuClose()
		}
	}

	if (!breadcrumbs && !hasLoan) return null

	const content = (function renderContent() {
		if (breadcrumbs) {
			return (
				<Breadcrumbs
					items={[...baseNavigationCrumbs, ...breadcrumbs]}
				/>
			)
		}

		return (
			<div className="flex flex-row items-center">
				<LoanLockedAlert className="flex flex-row items-center mr-2" />
				<LoanSummary />
			</div>
		)
	})()

	return (
		<div className="pr-4 py-2">
			<div className="flex flex-row items-center justify-between">
				{content}
				{isLoanAdmin && isPosLoan && (
					<ActionMenu
						open={isActionMenuOpen}
						onOpen={handleActionMenuOpen}
						onClose={handleActionMenuClose}
					>
						<MenuItem onClick={handleLoanSettingsClick}>
							{formatMessage({ id: 'loan_settings.heading' })}
						</MenuItem>
						{hasLoan && (
							<MenuItem
								onClick={handleSyncWithLosClick}
								disabled={importFromEncompassMutation.isPending}
							>
								{formatMessage({ id: 'loans.sync_with_los' })}
								{importFromEncompassMutation.isPending && (
									<CircularProgress size={12} className="ml-2" />
								)}
							</MenuItem>
						)}
					</ActionMenu>
				)}
			</div>
		</div>
	)
}
